import { useState, useEffect } from 'react';
import { FileText, Download, Trash2, Search, Filter, AlertCircle, Info, AlertTriangle, RefreshCw } from 'lucide-react';
import { toast } from '../utils/toast';

interface LogEntry {
  timestamp: string;
  level: 'log' | 'error' | 'warn';
  message: string;
  data?: any;
}

interface LogViewerProps {
  onClose: () => void;
}

export function LogViewer({ onClose }: LogViewerProps) {
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [filteredLogs, setFilteredLogs] = useState<LogEntry[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [levelFilter, setLevelFilter] = useState<'all' | 'log' | 'error' | 'warn'>('all');
  const [expandedLog, setExpandedLog] = useState<number | null>(null);

  useEffect(() => {
    loadLogs();
  }, []);

  useEffect(() => {
    filterLogs();
  }, [logs, searchQuery, levelFilter]);

  const loadLogs = () => {
    try {
      const storedLogs = localStorage.getItem('talentfunn_dev_logs');
      if (storedLogs) {
        const parsed = JSON.parse(storedLogs);
        const allLogs = parsed.logs || [];
        setLogs(allLogs.reverse()); // Newest first
      } else {
        setLogs([]);
      }
    } catch (error) {
      console.error('Failed to load logs:', error);
      toast.error('Kunne ikke laste logger');
    }
  };

  const filterLogs = () => {
    let filtered = logs;

    // Filter by level
    if (levelFilter !== 'all') {
      filtered = filtered.filter(log => log.level === levelFilter);
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(log => 
        log.message.toLowerCase().includes(query) ||
        JSON.stringify(log.data).toLowerCase().includes(query)
      );
    }

    setFilteredLogs(filtered);
  };

  const handleExport = () => {
    try {
      const logsToExport = filteredLogs.length > 0 ? filteredLogs : logs;
      const blob = new Blob([JSON.stringify({ logs: logsToExport }, null, 2)], { 
        type: 'application/json' 
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `talentfunn-logs-${new Date().toISOString().split('T')[0]}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Logger eksportert!');
    } catch (error) {
      console.error('Failed to export logs:', error);
      toast.error('Kunne ikke eksportere logger');
    }
  };

  const handleClearLogs = () => {
    if (confirm('Er du sikker på at du vil slette alle logger? Dette kan ikke angres.')) {
      localStorage.removeItem('talentfunn_dev_logs');
      setLogs([]);
      toast.success('Alle logger slettet');
    }
  };

  const handleGenerateTestLogs = () => {
    const testLogs: LogEntry[] = [
      {
        timestamp: new Date(Date.now() - 60000).toISOString(),
        level: 'log',
        message: 'User navigated to home page',
        data: { view: 'home', userId: 'test-123' }
      },
      {
        timestamp: new Date(Date.now() - 55000).toISOString(),
        level: 'log',
        message: 'Search filter applied',
        data: { category: 'Grafisk design', county: 'Oslo' }
      },
      {
        timestamp: new Date(Date.now() - 50000).toISOString(),
        level: 'warn',
        message: 'Deprecated API endpoint used',
        data: { endpoint: '/api/old-search', replacement: '/api/search/v2' }
      },
      {
        timestamp: new Date(Date.now() - 45000).toISOString(),
        level: 'log',
        message: 'Profile viewed',
        data: { profileId: 'abc-123', userType: 'talent' }
      },
      {
        timestamp: new Date(Date.now() - 40000).toISOString(),
        level: 'error',
        message: 'Failed to send message',
        data: { error: '403 Forbidden', reason: 'User lacks messagingAccess' }
      },
      {
        timestamp: new Date(Date.now() - 35000).toISOString(),
        level: 'log',
        message: 'Message input changed',
        data: { text: 'Hei, jeg er interessert i...', recipientId: 'def-456' }
      },
      {
        timestamp: new Date(Date.now() - 30000).toISOString(),
        level: 'warn',
        message: 'Subscription upgrade required',
        data: { feature: 'messaging', currentPlan: 'free', requiredPlan: 'employer' }
      },
      {
        timestamp: new Date(Date.now() - 25000).toISOString(),
        level: 'error',
        message: 'Network request failed',
        data: { url: '/functions/v1/talentfunn-server/profiles', status: 500 }
      },
      {
        timestamp: new Date(Date.now() - 20000).toISOString(),
        level: 'log',
        message: 'User clicked upgrade button',
        data: { planType: 'employer', priceId: 'price_employer_monthly' }
      },
      {
        timestamp: new Date(Date.now() - 15000).toISOString(),
        level: 'log',
        message: 'Checkout session created',
        data: { sessionId: 'cs_test_123', amount: 490 }
      },
      {
        timestamp: new Date(Date.now() - 10000).toISOString(),
        level: 'warn',
        message: 'Rate limit approaching',
        data: { endpoint: '/api/search', requests: 95, limit: 100 }
      },
      {
        timestamp: new Date(Date.now() - 5000).toISOString(),
        level: 'log',
        message: 'User logged out',
        data: { userId: 'test-123', sessionDuration: '15m 32s' }
      }
    ];

    const existingData = JSON.parse(localStorage.getItem('talentfunn_dev_logs') || '{"logs":[]}');
    const newLogs = [...existingData.logs, ...testLogs];
    localStorage.setItem('talentfunn_dev_logs', JSON.stringify({ logs: newLogs }));
    
    loadLogs();
    toast.success('12 test-logger generert!');
  };

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    const today = new Date();
    const isToday = date.toDateString() === today.toDateString();
    
    if (isToday) {
      return date.toLocaleTimeString('nb-NO', { 
        hour: '2-digit', 
        minute: '2-digit', 
        second: '2-digit' 
      });
    } else {
      return date.toLocaleString('nb-NO', { 
        month: 'short', 
        day: 'numeric', 
        hour: '2-digit', 
        minute: '2-digit' 
      });
    }
  };

  const getLevelIcon = (level: string) => {
    switch (level) {
      case 'error':
        return <AlertCircle className="w-4 h-4 text-red-500" />;
      case 'warn':
        return <AlertTriangle className="w-4 h-4 text-yellow-500" />;
      default:
        return <Info className="w-4 h-4 text-blue-500" />;
    }
  };

  const getLevelColor = (level: string) => {
    switch (level) {
      case 'error':
        return 'bg-red-50 border-red-200';
      case 'warn':
        return 'bg-yellow-50 border-yellow-200';
      default:
        return 'bg-blue-50 border-blue-200';
    }
  };

  const stats = {
    total: logs.length,
    errors: logs.filter(l => l.level === 'error').length,
    warnings: logs.filter(l => l.level === 'warn').length,
    info: logs.filter(l => l.level === 'log').length,
  };

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50" 
        style={{ zIndex: 'var(--z-important)' }}
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 flex items-center justify-center p-4 pt-safe pb-24 pb-safe" style={{ zIndex: 'var(--z-important)' }}>
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[90vh] flex flex-col">
          
          {/* Header */}
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <FileText className="w-6 h-6 text-purple-600" />
                <h2 className="text-2xl text-gray-800">Dev Logger</h2>
              </div>
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-4 gap-4 mb-4">
              <div className="bg-gray-50 rounded-xl p-3 border border-gray-200">
                <p className="text-xs text-gray-600 mb-1">Total</p>
                <p className="text-2xl font-semibold text-gray-800">{stats.total}</p>
              </div>
              <div className="bg-red-50 rounded-xl p-3 border border-red-200">
                <p className="text-xs text-red-600 mb-1">Errors</p>
                <p className="text-2xl font-semibold text-red-700">{stats.errors}</p>
              </div>
              <div className="bg-yellow-50 rounded-xl p-3 border border-yellow-200">
                <p className="text-xs text-yellow-600 mb-1">Warnings</p>
                <p className="text-2xl font-semibold text-yellow-700">{stats.warnings}</p>
              </div>
              <div className="bg-blue-50 rounded-xl p-3 border border-blue-200">
                <p className="text-xs text-blue-600 mb-1">Info</p>
                <p className="text-2xl font-semibold text-blue-700">{stats.info}</p>
              </div>
            </div>

            {/* Filters */}
            <div className="flex gap-3">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Søk i logger..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
              </div>
              <select
                value={levelFilter}
                onChange={(e) => setLevelFilter(e.target.value as any)}
                className="px-4 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-purple-500"
              >
                <option value="all">Alle nivåer</option>
                <option value="log">Info</option>
                <option value="warn">Warnings</option>
                <option value="error">Errors</option>
              </select>
              <button
                onClick={loadLogs}
                className="px-4 py-2 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-colors flex items-center gap-2"
                title="Refresh logs"
              >
                <RefreshCw className="w-4 h-4" />
              </button>
              <button
                onClick={handleExport}
                className="px-4 py-2 bg-blue-100 text-blue-700 rounded-xl hover:bg-blue-200 transition-colors flex items-center gap-2"
              >
                <Download className="w-4 h-4" />
                Eksporter
              </button>
              <button
                onClick={handleClearLogs}
                className="px-4 py-2 bg-red-100 text-red-700 rounded-xl hover:bg-red-200 transition-colors flex items-center gap-2"
              >
                <Trash2 className="w-4 h-4" />
                Slett alle
              </button>
              <button
                onClick={handleGenerateTestLogs}
                className="px-4 py-2 bg-green-100 text-green-700 rounded-xl hover:bg-green-200 transition-colors flex items-center gap-2"
              >
                <FileText className="w-4 h-4" />
                Generer test-logger
              </button>
            </div>
          </div>

          {/* Logs List */}
          <div className="flex-1 overflow-y-auto p-6 space-y-2">
            {filteredLogs.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-600">
                  {logs.length === 0 ? 'Ingen logger ennå' : 'Ingen logger matcher filtrene'}
                </p>
              </div>
            ) : (
              filteredLogs.map((log, index) => (
                <div
                  key={index}
                  className={`border rounded-xl p-4 transition-all cursor-pointer hover:shadow-md ${getLevelColor(log.level)}`}
                  onClick={() => setExpandedLog(expandedLog === index ? null : index)}
                >
                  <div className="flex items-start gap-3">
                    {getLevelIcon(log.level)}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-3 mb-1">
                        <p className="font-medium text-gray-800 break-words">{log.message}</p>
                        <span className="text-xs text-gray-500 whitespace-nowrap">
                          {formatTimestamp(log.timestamp)}
                        </span>
                      </div>
                      
                      {/* Expanded data */}
                      {expandedLog === index && log.data && (
                        <div className="mt-3 pt-3 border-t border-gray-300">
                          <p className="text-xs text-gray-600 mb-2 font-medium">Data:</p>
                          <pre className="text-xs bg-white/50 p-3 rounded-lg overflow-x-auto border border-gray-300">
                            {JSON.stringify(log.data, null, 2)}
                          </pre>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Footer */}
          <div className="p-4 border-t border-gray-200 bg-gray-50 text-center text-sm text-gray-600">
            Viser {filteredLogs.length} av {logs.length} logger
            {logs.length > 0 && (
              <span className="ml-2">
                • Logger slettes automatisk etter 7 dager
              </span>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
